<template>
  <main class="presenter-shell">
    <QueueSyncStatus :state="syncState" />
    <p v-if="error" role="alert">{{ error }}</p>
    <section class="layout-grid">
      <article class="card preview-card">
        <div class="panel-header">
          <div>
            <p class="panel-label">Next Up</p>
          </div>
        </div>

        <template v-if="previewItem">
          <div class="content-shell" :style="shellStyle(previewConfig)">
            <p
              v-if="showTypeLabel(previewItem, previewConfig)"
              class="item-type"
              :style="{ textAlign: previewConfig.alignment }"
            >
              {{ previewItem.type.replace("_", " ") }}
            </p>

            <template v-if="previewItem.type === 'student'">
              <h2 class="student-name" :style="headlineStyle(previewConfig)">
                {{ headlineValue(previewItem, previewConfig) }}
              </h2>
              <p
                v-if="sublineValue(previewItem, previewConfig)"
                class="student-award"
                :class="{ 'phonetics-subline': isPhoneticsSubline(previewConfig) }"
                :style="sublineStyle(previewConfig)"
              >
                {{ sublineValue(previewItem, previewConfig) }}
              </p>
            </template>

            <template v-else>
              <div class="narrative-copy" :style="bodyStyle(previewItem, previewConfig)">
                {{ bodyValue(previewItem, previewConfig) }}
              </div>
            </template>
          </div>
        </template>

        <div v-else class="empty-state">No preview item is set.</div>
      </article>

      <article class="card live-card">
        <div class="panel-header">
          <div>
            <p class="panel-label">Live Output</p>
          </div>
        </div>

        <template v-if="visibleItem">
          <div class="content-shell compact" :style="shellStyle(visibleConfig)">
            <p
              v-if="showTypeLabel(visibleItem, visibleConfig)"
              class="item-type"
              :style="{ textAlign: visibleConfig.alignment }"
            >
              {{ visibleItem.type.replace("_", " ") }}
            </p>

            <template v-if="visibleItem.type === 'student'">
              <h3 class="student-name compact" :style="headlineStyle(visibleConfig)">
                {{ headlineValue(visibleItem, visibleConfig) }}
              </h3>
              <p
                v-if="sublineValue(visibleItem, visibleConfig)"
                class="student-award compact"
                :class="{ 'phonetics-subline': isPhoneticsSubline(visibleConfig) }"
                :style="sublineStyle(visibleConfig)"
              >
                {{ sublineValue(visibleItem, visibleConfig) }}
              </p>
            </template>

            <template v-else>
              <div class="narrative-copy compact" :style="bodyStyle(visibleItem, visibleConfig, true)">
                {{ bodyValue(visibleItem, visibleConfig) }}
              </div>
              <p v-if="visibleItem.type === 'presenter_note'" class="lower-thirds-note">
                Presenter only. Lower thirds are blank for this row.
              </p>
            </template>
          </div>
        </template>

        <div v-else class="empty-state">Nothing has been announced yet.</div>
      </article>
    </section>

    <div class="action-row">
      <button
        class="announce-button"
        :class="{ confirming: previewItem && syncState.updating }"
        :aria-busy="syncState.updating ? 'true' : undefined"
        :disabled="!previewItem || !canOperate"
        @click="pushLive"
      >
        Announced
      </button>
    </div>
  </main>
</template>

<script>
import { pushPreviewLive } from "@/lib/api";
import queueSyncMixin from "@/lib/queueSyncMixin";
import {
  cloneViews,
  DEFAULT_VIEWS,
  presenterFontStyles,
  presenterWidthValue,
  resolveDisplayValue,
} from "@/lib/viewConfig";

export default {
  name: "PresenterView",
  mixins: [queueSyncMixin],
  data() {
    return {
      snapshot: null,
      error: "",
      viewConfig: cloneViews(DEFAULT_VIEWS),
      lastQueueRevision: 0,
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    queueItems() {
      return (this.snapshot?.queue?.items || []).map((item) =>
        this.withParticipantExtraFields(item)
      );
    },
    previewIndex() {
      const value = this.snapshot?.queue?.previewIndex;
      return value === null || value === undefined ? null : value;
    },
    visibleIndex() {
      const value = this.snapshot?.queue?.visibleIndex;
      return value === null || value === undefined ? null : value;
    },
    previewItem() {
      return this.previewIndex === null
        ? null
        : this.queueItems[this.previewIndex] || null;
    },
    visibleItem() {
      return this.visibleIndex === null
        ? null
        : this.queueItems[this.visibleIndex] || null;
    },
    previewConfig() {
      return this.configForItem(this.previewItem);
    },
    visibleConfig() {
      return this.configForItem(this.visibleItem);
    },
  },
  methods: {
    withParticipantExtraFields(item) {
      if (!item || item.extraFields) {
        return item;
      }
      const participantId = item.sourceParticipantId || item.id || "";
      if (!participantId) {
        return item;
      }
      const participant = (this.snapshot?.participants?.items || []).find(
        (candidate) => candidate.id === participantId
      );
      const extraFields = participant?.extraFields || {};
      if (!Object.keys(extraFields).length) {
        return item;
      }
      return {
        ...item,
        extraFields,
      };
    },
    configForItem(item) {
      if (!item) {
        return this.viewConfig.presenter.student;
      }
      if (item.type === "student") {
        return this.viewConfig.presenter.student;
      }
      if (item.type === "presenter_note") {
        return this.viewConfig.presenter.presenterNote;
      }
      return this.viewConfig.presenter.displayLine;
    },
    shellStyle(config) {
      const maxWidth = presenterWidthValue(config.maxWidth);
      return {
        textAlign: config.alignment,
        maxWidth,
        marginLeft: config.alignment === "center" ? "auto" : "0",
        marginRight: config.alignment === "center" ? "auto" : "0",
      };
    },
    headlineStyle(config) {
      const fonts = presenterFontStyles(config.fontScale);
      return {
        fontSize: fonts.headline,
        textAlign: config.alignment,
      };
    },
    sublineStyle(config) {
      const fonts = presenterFontStyles(config.fontScale);
      return {
        fontSize: fonts.subline,
        textAlign: config.alignment,
      };
    },
    bodyStyle(item, config, compact = false) {
      const fonts = presenterFontStyles(config.fontScale);
      return {
        fontSize: this.scaledBodyFontSize(item, fonts.body, compact),
        textAlign: config.alignment,
      };
    },
    scaledBodyFontSize(item, baseFontSize, compact = false) {
      if (!item || item.type !== "presenter_note") {
        return baseFontSize;
      }

      const baseSize = Number.parseFloat(baseFontSize);
      if (Number.isNaN(baseSize)) {
        return baseFontSize;
      }

      const textLength = this.bodyValue(item, this.configForItem(item)).trim().length;
      let scale = compact ? 0.92 : 1;

      if (textLength > 220) {
        scale *= 0.56;
      } else if (textLength > 160) {
        scale *= 0.66;
      } else if (textLength > 110) {
        scale *= 0.76;
      } else if (textLength > 70) {
        scale *= 0.88;
      }

      const nextSize = Math.max(baseSize * scale, compact ? 1.1 : 1.35);
      return `${nextSize.toFixed(2)}rem`;
    },
    showTypeLabel(item, config) {
      return item?.type === "student" && config.showTypeLabel;
    },
    headlineValue(item, config) {
      if (!item || item.type !== "student") {
        return "";
      }
      return resolveDisplayValue(item, config.headlineField);
    },
    sublineValue(item, config) {
      if (!item || item.type !== "student") {
        return "";
      }
      return resolveDisplayValue(item, config.sublineField);
    },
    isPhoneticsSubline(config) {
      return ["presenterPhonetics", "activePhonetics", "manualPhonetics"].includes(
        config?.sublineField
      );
    },
    bodyValue(item, config) {
      if (!item || item.type === "student") {
        return "";
      }
      return resolveDisplayValue(item, config.bodyField);
    },
    queueRevisionFor(queue) {
      const revision = Number.parseInt(queue?.revision || 0, 10);
      return Number.isNaN(revision) ? 0 : revision;
    },
    applySnapshot(snapshot, { includeViews = false } = {}) {
      const incomingQueueRevision = this.queueRevisionFor(snapshot.queue);
      const currentQueueRevision = Math.max(
        this.lastQueueRevision,
        this.queueRevisionFor(this.snapshot?.queue)
      );
      const shouldKeepCurrentQueue =
        this.snapshot?.queue && incomingQueueRevision < currentQueueRevision;
      const nextQueue = shouldKeepCurrentQueue ? this.snapshot.queue : snapshot.queue;

      if (!this.snapshot) {
        this.snapshot = {
          ...snapshot,
          queue: nextQueue,
        };
      } else {
        this.snapshot = {
          ...this.snapshot,
          event: snapshot.event,
          participants: snapshot.participants,
          queue: nextQueue,
          workflow: snapshot.workflow,
        };
      }

      if (includeViews) {
        this.viewConfig = cloneViews(snapshot.views || DEFAULT_VIEWS, snapshot.event);
      }
      if (!shouldKeepCurrentQueue) {
        this.lastQueueRevision = incomingQueueRevision;
      }
    },

    async pushLive() {
      await this.runQueueOperation(payload => pushPreviewLive(this.eventId, payload));
    },
  },
  async mounted() {
    this.startQueueSync();
  },
};
</script>

<style scoped>
:deep(.queue-sync-status.live),
:deep(.queue-sync-status.updating) {
  background: transparent;
  color: var(--orator-graphite);
}

.presenter-shell {
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem 1rem 7.5rem;
}

.layout-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
}

.card {
  background: white;
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid var(--orator-smoke);
  min-height: 18rem;
}

.preview-card {
  min-height: 22rem;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.panel-label {
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 800;
  color: var(--orator-graphite);
}

.content-shell {
  width: 100%;
  margin-top: 1rem;
}

.content-shell.compact {
  margin-top: 0.75rem;
}

.item-type {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--orator-graphite);
  margin-bottom: 0.75rem;
}

.student-name {
  font-weight: 900;
  line-height: 1.05;
  color: var(--orator-navy);
}

.student-award {
  margin-top: 1rem;
  color: var(--orator-graphite);
  line-height: 1.15;
}

.student-award.phonetics-subline {
  margin-top: 1.55rem;
  word-spacing: 0.45em;
}

.student-name.compact {
  font-weight: 800;
}

.student-award.compact {
  margin-top: 0.75rem;
}

.student-award.compact.phonetics-subline {
  margin-top: 1.15rem;
}

.live-card .student-name,
.live-card .student-award,
.live-card .narrative-copy {
  color: var(--orator-graphite);
}

.narrative-copy {
  line-height: 1.2;
  font-weight: 700;
  color: var(--orator-navy);
  overflow-wrap: anywhere;
}

.narrative-copy.compact {
  font-weight: 600;
}

.lower-thirds-note {
  margin-top: 0.9rem;
  color: var(--orator-graphite);
  font-size: 0.95rem;
  font-weight: 600;
}

.empty-state {
  min-height: 12rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--orator-graphite);
  font-size: 1.15rem;
  text-align: center;
}

.action-row {
  position: sticky;
  bottom: 0;
  z-index: 20;
  margin-top: 1rem;
  padding-top: 1rem;
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(to top, rgba(248, 250, 252, 0.98), rgba(248, 250, 252, 0.86), transparent);
}

.announce-button {
  width: 100%;
  min-height: 5.5rem;
  border: none;
  border-radius: 1rem;
  background: var(--orator-blue);
  color: white;
  font-size: 1.9rem;
  font-weight: 900;
  letter-spacing: 0.01em;
  box-shadow: 0 14px 28px rgba(8, 105, 123, 0.22);
}

.announce-button:disabled {
  opacity: 0.55;
}

.announce-button.confirming:disabled {
  opacity: 1;
  cursor: progress;
}

@media (min-width: 960px) and (orientation: landscape) {
  .layout-grid {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  }

  .card {
    min-height: 24rem;
  }

  .announce-button {
    min-height: 6rem;
    font-size: 2.25rem;
  }
}
</style>
